import { useEffect, useState } from "react";
import { ImagePlus, Layers, Link2, LogOut, Package, Pencil, Plus, Receipt, Sparkles, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatARS } from "@/lib/format";
import { toast } from "sonner";

const EMPTY_FORM = {
  name: "",
  brand: "",
  category: "",
  price: "",
  original_price: "",
  badge: "",
  sizes: "",
  description: "",
  featured: false,
  images: [],
};

function toForm(p) {
  return {
    name: p.name || "",
    brand: p.brand || "",
    category: p.category || "",
    price: p.price ?? "",
    original_price: p.original_price ?? "",
    badge: p.badge || "",
    sizes: (p.sizes || []).join(", "),
    description: p.description || "",
    featured: !!p.featured,
    images: p.images || [],
  };
}

function ProductForm({ initial, categories, onSaved, onCancel }) {
  const editing = Boolean(initial?.id);
  const [form, setForm] = useState(initial ? toForm(initial) : EMPTY_FORM);
  const [urlInput, setUrlInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "w-full border border-white/15 bg-panel-2 px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-volt";

  const uploadFiles = async (files) => {
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      } catch (err) {
        toast.error(`Error subiendo imagen: ${formatApiErrorDetail(err.response?.data?.detail)}`);
      }
    }
    setUploading(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name,
      brand: form.brand,
      category: form.category,
      price: Number(form.price) || 0,
      original_price: form.original_price ? Number(form.original_price) : null,
      badge: form.badge || null,
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      description: form.description,
      featured: form.featured,
      images: form.images,
    };
    try {
      if (editing) await api.put(`/admin/productos/${initial.id}`, payload);
      else await api.post("/admin/productos", payload);
      toast.success(editing ? "Producto actualizado" : "Producto creado");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="border border-volt/30 bg-panel p-6" data-testid="admin-product-form">
      <div className="flex items-center justify-between">
        <p className="font-display text-xl font-black uppercase tracking-wide text-white">
          {editing ? "Editar producto" : "Nuevo producto"}
        </p>
        <button type="button" onClick={onCancel} data-testid="admin-product-form-cancel-button" aria-label="Cerrar" className="text-zinc-500 hover:text-white">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <input required data-testid="admin-input-name" value={form.name} onChange={set("name")} placeholder="Nombre del producto *" className={`${inputCls} sm:col-span-2`} />
        <div>
          <input required list="mm-categories" data-testid="admin-input-category" value={form.category} onChange={set("category")} placeholder="Categoría (Ropa, Perfumes...) *" className={inputCls} />
          <datalist id="mm-categories">
            {categories.map((c) => (
              <option key={c.name} value={c.name} />
            ))}
          </datalist>
        </div>
        <input data-testid="admin-input-brand" value={form.brand} onChange={set("brand")} placeholder="Marca (Nike, Adidas...)" className={inputCls} />
        <input required type="number" min="0" data-testid="admin-input-price" value={form.price} onChange={set("price")} placeholder="Precio en ARS *" className={inputCls} />
        <input type="number" min="0" data-testid="admin-input-original-price" value={form.original_price} onChange={set("original_price")} placeholder="Precio anterior (opcional)" className={inputCls} />
        <input data-testid="admin-input-badge" value={form.badge} onChange={set("badge")} placeholder="Etiqueta (NUEVO, MÁS VENDIDO...)" className={inputCls} />
        <input data-testid="admin-input-sizes" value={form.sizes} onChange={set("sizes")} placeholder="Talles separados por coma: S, M, L" className={inputCls} />
        <textarea data-testid="admin-input-description" value={form.description} onChange={set("description")} placeholder="Descripción" rows={3} className={`${inputCls} sm:col-span-2`} />
        <label className="flex items-center gap-3 text-sm text-zinc-300 sm:col-span-2">
          <input type="checkbox" data-testid="admin-input-featured" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} className="h-4 w-4 accent-volt" />
          Mostrar en "Lo más vendido" de la home
        </label>
      </div>

      <div className="mt-5">
        <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Imágenes</p>
        <div className="mt-3 flex flex-wrap gap-3">
          {form.images.map((im, i) => (
            <div key={i} className="relative">
              <img src={im} alt="" className="h-20 w-20 border border-white/15 object-cover" />
              <button
                type="button"
                data-testid={`admin-image-remove-${i}`}
                onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, x) => x !== i) }))}
                className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white"
                aria-label="Quitar imagen"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row">
          <label className="inline-flex cursor-pointer items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-xs uppercase tracking-widest text-zinc-300 transition-colors hover:border-volt hover:text-volt">
            <ImagePlus className="h-4 w-4" /> {uploading ? "Subiendo..." : "Subir fotos"}
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { if (e.target.files?.length) uploadFiles(Array.from(e.target.files)); e.target.value = ""; }} data-testid="admin-image-file-input" />
          </label>
          <div className="flex flex-1 gap-2">
            <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="...o pegá una URL de imagen" className={inputCls} data-testid="admin-image-url-input" />
            <button
              type="button"
              data-testid="admin-image-url-add-button"
              onClick={() => { if (urlInput.trim()) { setForm((f) => ({ ...f, images: [...f.images, urlInput.trim()] })); setUrlInput(""); } }}
              className="flex items-center gap-1.5 border border-white/15 px-4 py-2.5 font-mono text-xs uppercase tracking-widest text-zinc-300 transition-colors hover:border-volt hover:text-volt"
            >
              <Link2 className="h-4 w-4" /> Agregar
            </button>
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={saving || uploading}
        data-testid="admin-product-form-save-button"
        className="mt-6 w-full bg-volt py-3.5 font-display text-sm font-black uppercase tracking-widest text-black transition-colors hover:bg-volt-dim disabled:opacity-50"
      >
        {saving ? "Guardando..." : editing ? "Guardar cambios" : "Crear producto"}
      </button>
    </form>
  );
}

function BulkImport({ onDone, onCancel }) {
  const [raw, setRaw] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const ordenarConIA = async () => {
    if (!raw.trim() || aiLoading) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/importar-texto", { texto: raw });
      if (!data.productos?.length) {
        toast.error("La IA no encontró productos en ese texto");
      } else {
        setRaw(data.productos.map((p) => `${p.name} | ${p.price} | ${p.category}`).join("\n"));
        toast.success(`La IA ordenó ${data.productos.length} productos`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setAiLoading(false);
  };

  const detectCat = (name) => {
    const n = name.toLowerCase();
    if (/perfume|yara|khamrah|árabe|arabe|esencia|colonia/.test(n)) return "Perfumes";
    if (/cadena|collar|gorra|reloj|lentes|billetera|cartera|aros|accesor|anillo|pulsera/.test(n)) return "Accesorios";
    return "Ropa";
  };
  const parsePrice = (s) => parseInt(String(s).replace(/[^\d]/g, ""), 10) || 0;

  const parsed = raw
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split("|").map((s) => s.trim());
      if (parts.length >= 2)
        return { name: parts[0], price: parsePrice(parts[1]), category: parts[2] || detectCat(parts[0]), image: parts[3] || "" };
      const m = line.match(/^(.*?)\s*[—–-]\s*\$?\s*([\d.,]+)\s*$/);
      if (m) return { name: m[1].trim(), price: parsePrice(m[2]), category: detectCat(m[1]), image: "" };
      return { name: line, price: 0, category: detectCat(line), image: "" };
    });
  const valid = parsed.filter((p) => p.name && p.price > 0);
  const invalid = parsed.filter((p) => !p.name || p.price <= 0);

  const submit = async () => {
    setSaving(true);
    try {
      const items = valid.map((p) => ({
        name: p.name,
        category: p.category,
        price: p.price,
        images: p.image ? [p.image] : [],
        description: "",
        sizes: [],
      }));
      const { data } = await api.post("/admin/productos/bulk", { items });
      toast.success(`${data.created} productos creados. Sumales las fotos editándolos.`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <div className="border border-volt/30 bg-panel p-6" data-testid="admin-bulk-panel">
      <div className="flex items-center justify-between">
        <p className="font-display text-xl font-black uppercase tracking-wide text-white">Carga masiva</p>
        <button type="button" onClick={onCancel} data-testid="admin-bulk-cancel-button" aria-label="Cerrar" className="text-zinc-500 hover:text-white">
          <X className="h-5 w-5" />
        </button>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400">
        Copiá tu lista del WhatsApp y pegala acá, <span className="text-white">un producto por línea</span>. Si está desordenada (con emojis, descripciones, texto suelto), tocá <span className="text-volt">"Ordenar con IA"</span> y yo la armo. Formatos directos:
      </p>
      <pre className="mt-3 overflow-x-auto border border-white/10 bg-panel-2 p-4 font-mono text-xs leading-relaxed text-volt">
{`Conjunto Nike | 50000
Short Adidas | 12000 | Ropa
Cadena Trenza | 18000 | Accesorios
Afnan 9PM | 45000 | Perfumes | https://foto.jpg
Campera Adidas — $55.000`}
      </pre>
      <p className="mt-2 text-xs text-zinc-500">
        Si no ponés categoría, la detecto sola (ropa/perfume/accesorio). Las fotos las sumás después editando cada producto, o pegá la URL en la 4ta columna.
      </p>
      <textarea
        data-testid="admin-bulk-textarea"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        rows={8}
        placeholder="Pegá tu lista acá, ordenada o como la tengas..."
        className="mt-4 w-full border border-white/15 bg-panel-2 px-4 py-3 font-mono text-sm text-white placeholder:text-zinc-600 outline-none focus:border-volt"
      />
      <button
        type="button"
        onClick={ordenarConIA}
        disabled={aiLoading || !raw.trim()}
        data-testid="admin-bulk-ai-button"
        className="mt-3 inline-flex items-center gap-2 border border-volt/50 px-5 py-2.5 font-display text-sm font-black uppercase tracking-widest text-volt transition-colors hover:bg-volt hover:text-black disabled:opacity-50"
      >
        <Sparkles className="h-4 w-4" /> {aiLoading ? "La IA está ordenando..." : "Ordenar con IA"}
      </button>
      {parsed.length > 0 && (
        <div className="mt-4 border border-white/10 bg-panel-2 p-4" data-testid="admin-bulk-preview">
          <p className="font-mono text-xs uppercase tracking-widest text-zinc-400">
            {valid.length} listos para importar{invalid.length > 0 && ` · ${invalid.length} sin precio válido`}:
          </p>
          <div className="mt-2 max-h-44 space-y-1 overflow-y-auto">
            {parsed.map((p, i) => (
              <p key={i} className={`font-mono text-xs ${p.price > 0 ? "text-zinc-300" : "text-red-400"}`}>
                {p.name} · {p.price > 0 ? formatARS(p.price) : "SIN PRECIO"} · {p.category}
              </p>
            ))}
          </div>
        </div>
      )}
      <button
        onClick={submit}
        disabled={saving || valid.length === 0}
        data-testid="admin-bulk-import-button"
        className="mt-5 w-full bg-volt py-3.5 font-display text-sm font-black uppercase tracking-widest text-black transition-colors hover:bg-volt-dim disabled:opacity-50"
      >
        {saving ? "Importando..." : `Importar ${valid.length} productos`}
      </button>
    </div>
  );
}

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-5 py-20">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/10 bg-panel p-8" data-testid="admin-login-form">
        <p className="font-mono text-[11px] uppercase tracking-widest text-volt">MM Store · Panel</p>
        <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tight text-white">Admin</h1>
        <input type="email" required data-testid="admin-login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="mt-6 w-full border border-white/15 bg-panel-2 px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-volt" />
        <input type="password" required data-testid="admin-login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña" className="mt-3 w-full border border-white/15 bg-panel-2 px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-volt" />
        {error && <p className="mt-3 text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="admin-login-submit-button" className="mt-5 w-full bg-volt py-3.5 font-display text-sm font-black uppercase tracking-widest text-black transition-colors hover:bg-volt-dim disabled:opacity-50">
          {loading ? "Ingresando..." : "Ingresar"}
        </button>
      </form>
    </main>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("productos");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadAll = () => {
    api.get("/productos").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categorias").then((r) => setCategories(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) loadAll();
  }, [user]);

  useEffect(() => {
    if (user && tab === "pedidos") api.get("/admin/pedidos").then((r) => setOrders(r.data)).catch(() => {});
  }, [user, tab]);

  if (user === undefined) {
    return <p className="pt-40 text-center font-mono text-sm uppercase tracking-widest text-zinc-500">Cargando...</p>;
  }
  if (!user) return <LoginView onLogin={setUser} />;

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}" del catálogo?`)) return;
    try {
      await api.delete(`/admin/productos/${p.id}`);
      toast.success("Producto eliminado");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  return (
    <main className="mx-auto max-w-7xl px-5 pb-24 pt-28 sm:px-8">
      <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-volt">MM Store · Panel</p>
          <h1 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-white">Administración</h1>
        </div>
        <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 border border-white/15 px-5 py-2.5 font-mono text-xs uppercase tracking-widest text-zinc-400 transition-colors hover:border-red-400 hover:text-red-400">
          <LogOut className="h-4 w-4" /> Salir
        </button>
      </div>

      <div className="mt-8 flex gap-2">
        <button onClick={() => setTab("productos")} data-testid="admin-products-tab" className={`inline-flex items-center gap-2 px-5 py-2.5 font-display text-sm font-bold uppercase tracking-widest transition-colors ${tab === "productos" ? "bg-volt text-black" : "border border-white/15 text-zinc-400 hover:text-white"}`}>
          <Package className="h-4 w-4" /> Productos
        </button>
        <button onClick={() => setTab("pedidos")} data-testid="admin-orders-tab" className={`inline-flex items-center gap-2 px-5 py-2.5 font-display text-sm font-bold uppercase tracking-widest transition-colors ${tab === "pedidos" ? "bg-volt text-black" : "border border-white/15 text-zinc-400 hover:text-white"}`}>
          <Receipt className="h-4 w-4" /> Pedidos
        </button>
      </div>

      {tab === "productos" ? (
        <div className="mt-8">
          {showForm || editing ? (
            <ProductForm
              initial={editing}
              categories={categories}
              onCancel={() => { setShowForm(false); setEditing(null); }}
              onSaved={() => { setShowForm(false); setEditing(null); loadAll(); }}
            />
          ) : showBulk ? (
            <BulkImport
              onCancel={() => setShowBulk(false)}
              onDone={() => { setShowBulk(false); loadAll(); }}
            />
          ) : (
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowForm(true)}
                data-testid="admin-new-product-button"
                className="inline-flex items-center gap-2 bg-volt px-6 py-3.5 font-display text-sm font-black uppercase tracking-widest text-black transition-colors hover:bg-volt-dim"
              >
                <Plus className="h-5 w-5" /> Nuevo producto
              </button>
              <button
                onClick={() => setShowBulk(true)}
                data-testid="admin-bulk-open-button"
                className="inline-flex items-center gap-2 border border-volt/50 px-6 py-3.5 font-display text-sm font-black uppercase tracking-widest text-volt transition-colors hover:bg-volt hover:text-black"
              >
                <Layers className="h-5 w-5" /> Carga masiva
              </button>
            </div>
          )}

          <div className="mt-8 space-y-2" data-testid="admin-products-list">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 border border-white/10 bg-panel p-3" data-testid={`admin-product-row-${p.id}`}>
                <img src={p.images?.[0]} alt="" className="h-14 w-14 shrink-0 object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-base font-bold uppercase text-white">{p.name}</p>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                    {p.category} · {formatARS(p.price)}{p.featured ? " · destacado" : ""}
                  </p>
                </div>
                <button onClick={() => { setEditing(p); setShowForm(false); window.scrollTo(0, 0); }} data-testid={`admin-product-edit-${p.id}`} aria-label={`Editar ${p.name}`} className="flex h-9 w-9 items-center justify-center border border-white/15 text-zinc-400 transition-colors hover:border-volt hover:text-volt">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(p)} data-testid={`admin-product-delete-${p.id}`} aria-label={`Eliminar ${p.name}`} className="flex h-9 w-9 items-center justify-center border border-white/15 text-zinc-400 transition-colors hover:border-red-400 hover:text-red-400">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-8 space-y-2" data-testid="admin-orders-list">
          {orders.length === 0 ? (
            <p className="border border-dashed border-white/15 py-16 text-center font-mono text-sm uppercase tracking-widest text-zinc-500">
              Todavía no hay pedidos
            </p>
          ) : (
            orders.map((o) => (
              <div key={o.order_id} className="flex flex-wrap items-center gap-x-6 gap-y-2 border border-white/10 bg-panel p-4" data-testid={`admin-order-row-${o.order_id}`}>
                <p className="font-mono text-xs font-bold text-volt">{o.order_id}</p>
                <p className="text-sm text-white">{o.buyer?.name}</p>
                <p className="text-sm text-zinc-400">{o.buyer?.phone}</p>
                <p className="font-mono text-sm text-zinc-300">{formatARS(o.total)}</p>
                <p className={`font-mono text-[11px] uppercase tracking-widest ${o.status === "approved" ? "text-volt" : o.status === "rejected" ? "text-red-400" : "text-zinc-500"}`} data-testid={`admin-order-status-${o.order_id}`}>
                  {o.status}
                </p>
                <p className="ml-auto font-mono text-[11px] uppercase tracking-widest text-zinc-600">{o.payment_method}</p>
              </div>
            ))
          )}
        </div>
      )}
    </main>
  );
}
